import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import type { StageMessage } from './shared';
import { useProtectionActivity } from './use-protection-activity';

/**
 * "V2" banner — corn only, advisor-facing.
 *
 * Shown to advisor-type users when an active corn crop monitoring is in the V2 stage
 * (`phenology === 'emergence'` with `vegetativeStage === 2`). Two mutually exclusive messages:
 *
 * - No NSmart (nitrogen) recommendation yet → nudge to request one before the fertilization
 *   window (actions: register application / request NSmart).
 * - NSmart exists but no fungicide/insecticide application or recommendation yet → nudge to
 *   protect yield against pests/diseases (actions: register application / generate
 *   recommendation).
 *
 * Returns `null` outside V2, for non-advisor users, or once NSmart exists and some
 * fungicide/insecticide activity is registered.
 */
export const useV2StageMessage = ({
    cropMonitoring,
    field,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
}): StageMessage | null => {
    const isValidatedBayerUserType = useIsValidatedBayerUserType();

    const corn = cropMonitoring?.cornCropMonitoring;
    const isCornV2 =
        Boolean(cropMonitoring?.active) &&
        cropMonitoring?.crop === 'corn' &&
        corn?.phenology === 'emergence' &&
        corn?.vegetativeStage === 2;

    const showCandidate = isValidatedBayerUserType && isCornV2;

    // `nitrogenRecomendations` is an array of ids on the CM payload — its presence alone tells us
    // an NSmart recommendation exists; no separate fetch needed.
    const hasNSmart = (cropMonitoring?.nitrogenRecomendations?.length ?? 0) > 0;

    // Fungicide/insecticide activity only matters for the second message (NSmart already exists).
    const protection = useProtectionActivity({ cropMonitoring, field, enabled: showCandidate && hasNSmart });

    if (!showCandidate) return null;

    // First message — no NSmart recommendation yet. Decided from the CM payload, no fetch needed.
    if (!hasNSmart) {
        return {
            title: _('Se aproxima la ventana de fertilización'),
            message: _(
                'Pedí la recomendación de NSmart al equipo técnico para planificar la refertilización del lote con el productor.'
            ),
            actions: [
                { label: _('Registrar aplicación'), variant: 'contained', action: 'register-application' },
                { label: _('Pedir recomendación'), variant: 'outlined', action: 'request-nsmart' },
            ],
        };
    }

    // Second message — NSmart exists; prompt to protect yield while no fungicide/insecticide
    // activity is registered. Hold until the gating queries resolve.
    if (protection.isLoading) return null;
    if (protection.hasActivity) return null;

    return {
        title: _('Ayudá a proteger el rendimiento del cultivo'),
        message: _(
            'Acompañá al productor a detectar plagas y enfermedades a tiempo y recomendá el producto para controlarlas.'
        ),
        actions: [
            { label: _('Registrar aplicación'), variant: 'contained', action: 'register-application' },
            { label: _('Generar recomendación'), variant: 'outlined', action: 'generate-cp-recommendation' },
        ],
    };
};
